/**
 * Feature flag values from central-service (`companies.<id>.features` in its database), per
 * company, mirroring the app's (shelf-app/app/shared/company/featureOverrides.js).
 *
 * Kept in localStorage so a page load applies the last values right away; `data/central.js`
 * fetches fresh ones. Flags hold no user data, so a company reset keeps them (reset.js).
 * No imports from the session: `./companies.js` reads this module.
 */

export const FEATURE_FLAGS_STORAGE_KEY = 'shelf.featureFlags';
const FEATURE_NAME_PATTERN = /^[a-z][a-zA-Z0-9]{0,63}$/;

// undefined: not loaded; otherwise companyId -> frozen { flag: boolean }
let overrides;

/**
 * Keep only `name: boolean` pairs; anything else is dropped.
 * @param {any} raw
 * @returns {Readonly<Record<string, boolean>>}
 */
export function sanitizeFeatureMap(raw) {
    const clean = {};
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return Object.freeze(clean);
    for (const [name, value] of Object.entries(raw)) {
        if (FEATURE_NAME_PATTERN.test(name) && typeof value === 'boolean') clean[name] = value;
    }
    return Object.freeze(clean);
}

function storage() {
    try {
        return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    } catch {
        return null;
    }
}

function load() {
    if (overrides !== undefined) return overrides;
    overrides = new Map();
    try {
        const parsed = JSON.parse(storage()?.getItem(FEATURE_FLAGS_STORAGE_KEY) || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            for (const [companyId, features] of Object.entries(parsed)) overrides.set(companyId, sanitizeFeatureMap(features));
        }
    } catch {
        // a broken cache means built-in values until the next fetch
    }
    return overrides;
}

function save() {
    try {
        storage()?.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(Object.fromEntries(load())));
    } catch {
        // ignore: values still apply for this page
    }
}

/**
 * Replace `companyId`'s remote values; `null` forgets them (built-in values apply).
 * @returns {boolean} Whether anything changed
 */
export function setFeatureOverrides(companyId, features) {
    if (typeof companyId !== 'string' || !companyId) return false;
    const map = load();
    const before = JSON.stringify(map.get(companyId) ?? null);
    if (features === null) map.delete(companyId);
    else map.set(companyId, sanitizeFeatureMap(features));
    const changed = before !== JSON.stringify(map.get(companyId) ?? null);
    if (changed) save();
    return changed;
}

/**
 * The remote value of `feature` for `companyId`, or undefined when central-service set none.
 * @returns {boolean|undefined}
 */
export function getFeatureOverride(companyId, feature) {
    const map = load().get(companyId);
    return map && Object.prototype.hasOwnProperty.call(map, feature) ? map[feature] : undefined;
}

/** Test helper: forget the in-memory copy so the next read goes to storage. */
export function __resetFeatureOverridesForTests() {
    overrides = undefined;
}
